import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import productData, { getProductName } from '../lib/productData';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { formatNumber } from '../lib/utils';

/**
 * Ürünler sayfası - Tüm ürünleri listeler
 * @returns {JSX.Element} Ürünler sayfası
 */
const Products = () => {
  const { t, i18n } = useTranslation();
  
  // Kontrol ünitesi olmayan ürünleri filtrele
  const extinguisherProducts = productData.filter(product => !product.isController);
  const controllerProducts = productData.filter(product => product.isController);
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">{t('products.title')}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">{t('products.subtitle')}</p>
        </div>
        
        <section className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">{t('products.extinguishers')}</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {extinguisherProducts.map(product => (
              <Card key={product.id} className="overflow-hidden border-2 hover:border-primary/20 transition-colors">
                <CardHeader className="pb-2 px-3 sm:px-6 bg-muted/10">
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                    <CardTitle className="text-lg sm:text-xl text-primary">
                      {product.model}
                    </CardTitle>
                    <Badge variant="outline" className="self-start sm:self-auto">
                      No: {product.id}
                    </Badge>
                  </div>
                  <CardDescription className="line-clamp-2 text-sm font-medium">{getProductName(product, i18n.language)}</CardDescription>
                </CardHeader>
                
                <CardContent className="pb-2 px-3 sm:px-6">
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <div className="flex items-baseline gap-1 mb-2">
                        <h3 className="text-sm sm:text-base font-bold text-black">{t('calculator.effective_mass_percentage')}:</h3>
                        <span className="text-xs sm:text-sm">%{product.effective_mass_percentage}</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <h3 className="text-sm sm:text-base font-bold text-black">{t('calculator.effective_mass')}:</h3>
                        <span className="text-xs sm:text-sm">{formatNumber(product.effective_mass)} g</span>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-black mb-1">{t('products.physical_properties')}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs sm:text-sm">
                        <div className="flex items-baseline gap-1">
                          <span className="text-muted-foreground">{t('calculator.total_mass')}:</span>
                          <span>{product.weight_kg} kg</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-muted-foreground">{t('calculator.contained_agent')}:</span>
                          <span>{formatNumber(product.contained_agent)} g</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-muted-foreground">{t('products.product_dimensions')}:</span>
                          <span>{product.dimensions_mm}</span>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-black mb-1">{t('products.suppression_capacities')}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs sm:text-sm">
                        <div className="flex items-baseline gap-1">
                          <span className="text-muted-foreground">{t('calculator.class_a_capacity')}:</span>
                          <span>{formatNumber(product.class_a_capacity)} m³</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-muted-foreground">{t('calculator.class_b_capacity')}:</span>
                          <span>{formatNumber(product.class_b_capacity)} m³</span>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-black mb-1">{t('products.mounting_info')}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs sm:text-sm">
                        <div className="flex items-baseline gap-1">
                          <span className="text-muted-foreground">{t('calculator.min_height')}:</span>
                          <span>{Math.round(product.min_height * 100)} cm</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-muted-foreground">{t('calculator.max_height')}:</span>
                          <span>{Math.round(product.max_height * 100)} cm</span>
                        </div>
                        {product.safe_distance > 0 && (
                          <div className="flex items-baseline gap-1 sm:col-span-2">
                            <span className="text-muted-foreground">{t('calculator.safe_distance_human')}:</span>
                            <span>{Math.round(product.safe_distance * 100)} cm</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
                
                <CardFooter className="flex flex-col sm:flex-row justify-between pt-1 border-t gap-1 px-3 sm:px-6 bg-muted/5">
                  <Badge variant="outline" className="self-start text-xs bg-background">{t('calculator.dimensions')}: {Math.round(product.width_mm)}×{Math.round(product.depth_mm)}×{Math.round(product.height_mm)} cm</Badge>
                  <Badge variant="secondary" className="self-end text-xs font-medium">{product.model}</Badge>
                </CardFooter>
              </Card>
            ))}
          </div>
        </section>
        
        {controllerProducts.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">{t('products.controllers')}</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {controllerProducts.map(product => (
                <Card key={product.id} className="overflow-hidden border-2 hover:border-primary/20 transition-colors">
                  <CardHeader className="pb-2 px-3 sm:px-6 bg-muted/10">
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                      <CardTitle className="text-lg sm:text-xl text-primary">
                        {product.model}
                      </CardTitle>
                      <Badge variant="outline" className="self-start sm:self-auto">
                        No: {product.id}
                      </Badge>
                    </div>
                    <CardDescription className="line-clamp-2 text-sm font-medium">{getProductName(product, i18n.language)}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pb-2 px-3 sm:px-6">
                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <h3 className="text-sm sm:text-base font-bold text-black mb-1">{t('products.controller_info')}</h3>
                        <div className="grid grid-cols-1 gap-1 text-xs sm:text-sm">
                          <div className="flex items-baseline gap-1">
                            <span className="text-muted-foreground">{t('products.max_extinguishers')}:</span>
                            <span>12</span>
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                            {t('products.controller_description')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  
                  <CardFooter className="flex flex-col sm:flex-row justify-between pt-1 border-t gap-1 px-3 sm:px-6 bg-muted/5">
                    <Badge variant="outline" className="self-start text-xs bg-background">{t('products.controller_unit')}</Badge>
                    <Badge variant="secondary" className="self-end text-xs font-medium">{product.model}</Badge>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </section>
        )}
        
        <section className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">{t('products.product_report')}</h2>
          
          <Card>
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="text-lg sm:text-xl">{t('products.general_analysis')}</CardTitle>
              <CardDescription className="text-sm">{t('products.analysis_description')}</CardDescription>
            </CardHeader>
            
            <CardContent className="px-3 sm:px-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-medium">{t('products.product_scope')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('products.product_scope_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-medium">{t('products.weight_range')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('products.weight_range_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-medium">{t('products.suppression_capacity')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('products.suppression_capacity_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-medium">{t('products.mounting_safety')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('products.mounting_safety_description')}
                  </p>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-medium">{t('products.calculation_formulas')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground whitespace-pre-line">
                    {t('products.calculation_formulas_description')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
      
      <footer className="py-6 sm:py-8 border-t bg-muted/20">
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} ADC Makine Sanayi ve Ticaret Limited Şirketi. Tüm hakları saklıdır.</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
              <Link 
                to="/privacy-policy" 
                className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('privacy.title')}
              </Link>
              <Link 
                to="/terms-of-service" 
                className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {t('terms.title')}
              </Link>
              <a 
                href="https://www.alasfire.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-primary hover:underline flex items-center"
              >
                <span>alasfire.com</span>
                <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Products;